/**
 * ReferralTile — the referral federation tile. The dashboard read federates the referral module's
 * per-médecin cards (no recompute, AN-0); when the referral module is unmounted on a local install the
 * server returns `referral: null` and this tile is HIDDEN entirely (D-28 clean degrade — the caller
 * renders nothing). When present it shows the active-médecin headline + a per-médecin returns table.
 */
import { Card, Table, type Column } from "@/ui";
import { useT } from "@/app";
import type { ReferralFederation } from "../types";

type Row = ReferralFederation["par_medecin"][number];

export function ReferralTile({ referral }: { referral: ReferralFederation }) {
  const { t } = useT();

  const columns: ReadonlyArray<Column<Row>> = [
    {
      key: "medecin",
      header: t("analytics.referral.col.medecin"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{String(r.medecin_id)}</span>,
    },
    {
      key: "retours",
      header: t("analytics.referral.col.retours"),
      mono: true,
      align: "end",
      sortable: true,
      sortAccessor: (r) => r.retours,
      cell: (r) => <span className="nv-ltr-island">{r.retours}</span>,
    },
  ];

  return (
    <Card title={t("analytics.referral.title")} padded={false}>
      <div style={{ padding: "var(--nv-s-3)", display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <p style={{ margin: 0, color: "var(--nv-slate)", fontSize: "var(--nv-fs-caption)" }}>
          {t("analytics.referral.subtitle")}
        </p>

        <div style={{ display: "flex", alignItems: "baseline", gap: "var(--nv-s-2)" }}>
          <span
            className="nv-mono nv-ltr-island"
            style={{ fontSize: "var(--nv-fs-h1)", fontWeight: 600, color: "var(--nv-ink)" }}
          >
            {referral.volume}
          </span>
          <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-slate)" }}>
            {t("analytics.referral.volume")}
          </span>
        </div>

        <Table
          columns={columns}
          rows={[...referral.par_medecin]}
          rowKey={(r) => String(r.medecin_id)}
          caption={t("analytics.referral.title")}
          emptyTitle={t("analytics.referral.empty")}
          pageSize={5}
        />
      </div>
    </Card>
  );
}
